import React, { useState, useEffect, useMemo } from "react"
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Stack,
  Chip,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  TextField,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import KitchenIcon from "@mui/icons-material/Kitchen"
import ExtensionIcon from "@mui/icons-material/Extension"
import StarIcon from "@mui/icons-material/Star"
import CountertopsIcon from "@mui/icons-material/Countertops"
import DoorFrontIcon from "@mui/icons-material/DoorFront"
import { useTranslation } from "react-i18next"
import { splitSwatches, PRICE_GROUP_OPTIONS } from "constants/catalogData"
import ProductGrid from "./ProductGrid"

const POSITION_LABELS = {
  Wall: "Κρεμαστα",
  Base: "Βασεως",
  Tall: "Ολοσωμα",
}
const POSITION_COLORS = {
  Wall: "#2563eb",
  Base: "#16a34a",
  Tall: "#7c3aed",
}

const MODE_CONFIG = {
  Cabinet: { label: "Κουτια", color: "#3b82f6", icon: KitchenIcon },
  Accessories: { label: "Αξεσουαρ", color: "#d97706", icon: ExtensionIcon },
  Special: { label: "Ειδικα", color: "#7c3aed", icon: StarIcon },
  Pagkos: { label: "Παγκος", color: "#78350f", icon: CountertopsIcon },
  DoorCladding: { label: "Πορτες Επενδυσης", color: "#0891b2", icon: DoorFrontIcon },
}

const AddBoxDialog = ({
  open,
  onClose,
  position,       // "Wall" | "Base" | "Tall" | null
  kasoma,
  priceGroup,     // global model code (initial value)
  swatch,         // global swatch object (initial value)
  pomolo,         // resolved pomolo from section header
  onAddProduct,
  productMode,    // "Cabinet" | "Accessories" | "Special"
  modelOptions,   // [{ code, label }]
  swatchesByGroup, // { "1M": [{ label, swatchUrl, hexColor }], ... }
  surcharges,     // { base_W, base_D, base_H, tall_W, tall_D, tall_H }
  initialModel,   // override model when opening from a cart item
  initialSwatch,  // override swatch when opening from a cart item
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  const effectivePomolo = pomolo || ""
  const mode = productMode || "Cabinet"
  const modeConfig = MODE_CONFIG[mode] || MODE_CONFIG.Cabinet
  const ModeIcon = modeConfig.icon
  const isCabinet = mode === "Cabinet"

  // ── Local price-group, color & door-model state — persists across modal open/close ──
  // Note: `localModel` here refers to the price-group code (1M, 5L, …).
  // `localDoorModel` is the door-shape swatch (e.g. "MODEL 519/19mm").
  const [localModel, setLocalModel] = useState(priceGroup || "")
  const [localSwatch, setLocalSwatch] = useState(swatch || null)
  const [localDoorModel, setLocalDoorModel] = useState(null)
  const [localRalCode, setLocalRalCode] = useState("")

  // RAL is available everywhere as reference. Dedup surcharge (€180 per unique
  // code, order-scoped) means repeated entries across items only charge once.
  const ralEligible = Boolean(localModel)

  // Specials use E1–E7 codes; worktops use P1-P3 (bakelite) plus E5/E6/E7 (lustre,
  // truly shared with specials via COLOR_FAMILIES). Other modes use the DB-driven
  // model list passed in via props.
  const effectiveModelOptions = useMemo(() => {
    if (mode === "Special") {
      return PRICE_GROUP_OPTIONS
        .filter(pg => pg.scope === "special")
        .map(pg => ({ code: pg.code, label: pg.label }))
    }
    if (mode === "Pagkos") {
      return PRICE_GROUP_OPTIONS
        .filter(pg => pg.scope === "worktop" || (pg.scope === "special" && ["E5","E6","E7"].includes(pg.code)))
        .map(pg => ({ code: pg.code, label: pg.label }))
    }
    return modelOptions || []
  }, [mode, modelOptions])

  // Only seed from parent when they actually change (not on every open)
  useEffect(() => {
    if (priceGroup && priceGroup !== localModel) setLocalModel(priceGroup)
  }, [priceGroup])
  useEffect(() => {
    if (swatch && swatch.label !== localSwatch?.label) setLocalSwatch(swatch)
  }, [swatch])

  // Seed from item overrides when dialog opens
  const [prevOpen, setPrevOpen] = useState(false)
  useEffect(() => {
    if (open && !prevOpen && initialModel) {
      setLocalModel(initialModel)
      setLocalSwatch(initialSwatch || null)
      setLocalDoorModel(null)
      setLocalRalCode("")
    }
    setPrevOpen(open)
  }, [open])

  // Available swatches for the selected price group — split into colors and door models
  const { currentColors, currentModels } = useMemo(() => {
    if (!localModel) return { currentColors: [], currentModels: [] }
    const flat = swatchesByGroup?.[localModel] || []
    const { colors, models } = splitSwatches(flat)
    return { currentColors: colors, currentModels: models }
  }, [localModel, swatchesByGroup])

  // When price group changes, reset color/door selections if they don't belong to it
  const handleModelChange = (newModel) => {
    setLocalModel(newModel)
    const flat = swatchesByGroup?.[newModel] || []
    if (localSwatch && !flat.some(sw => sw.label === localSwatch.label)) {
      setLocalSwatch(null)
    }
    if (localDoorModel && !flat.some(sw => sw.label === localDoorModel.label)) {
      setLocalDoorModel(null)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: fullScreen ? "100%" : "85vh", maxHeight: "85vh" },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          py: 0.75,
          px: 1.5,
          borderBottom: `2px solid ${modeConfig.color}20`,
          bgcolor: "#f8fafc",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#1e293b" }}>
              {t("Προσθηκη")}
            </Typography>

            {/* Mode badge */}
            <Chip
              icon={<ModeIcon sx={{ fontSize: 14 }} />}
              label={t(modeConfig.label)}
              size="small"
              sx={{
                bgcolor: `${modeConfig.color}15`,
                color: modeConfig.color,
                fontWeight: 700,
                fontSize: "0.7rem",
                height: 24,
                borderLeft: `3px solid ${modeConfig.color}`,
                "& .MuiChip-icon": { color: modeConfig.color },
              }}
            />

            {/* Position badge */}
            {isCabinet && position && (
              <Chip
                label={t(POSITION_LABELS[position] || position)}
                size="small"
                sx={{
                  bgcolor: POSITION_COLORS[position] || "#64748b",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: 24,
                }}
              />
            )}

            {isCabinet && (kasoma || effectivePomolo) && (
              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "#e2e8f0" }} />
            )}

            {/* Kasoma badge */}
            {isCabinet && kasoma && (
              <Chip
                label={`${t("Κασωμα")}: ${kasoma}`}
                size="small"
                sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
              />
            )}

            {/* Pomolo badge */}
            {isCabinet && effectivePomolo && (
              <Chip
                label={`${t("Πομολο")}: ${effectivePomolo}`}
                size="small"
                sx={{ bgcolor: "#f3e8ff", color: "#6b21a8", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
              />
            )}
          </Stack>
          <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {/* ── Model badges + Color dropdown ── */}
        {effectiveModelOptions.length > 0 && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75, pb: 0.5, flexWrap: "wrap" }}>
            {/* Model badge chips */}
            {effectiveModelOptions.map(opt => {
              const isActive = localModel === opt.code
              return (
                <Tooltip key={opt.code} title={opt.label} arrow placement="top">
                  <Chip
                    label={opt.code}
                    size="small"
                    variant={isActive ? "filled" : "outlined"}
                    onClick={() => handleModelChange(isActive ? "" : opt.code)}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      height: 28,
                      minWidth: 40,
                      cursor: "pointer",
                      ...(isActive
                        ? { bgcolor: "#e50019", color: "#fff", borderColor: "transparent" }
                        : { borderColor: "#d1d5db", color: "#475569" }),
                      "&:hover": isActive ? {} : { borderColor: "#e50019", color: "#e50019" },
                    }}
                  />
                </Tooltip>
              )
            })}

            {/* Door-model picker — door shape (e.g. MODEL 519, MODEL 521) */}
            {mode !== "Special" && localModel && currentModels.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0" }} />
                <FormControl size="small" sx={{ minWidth: 160, maxWidth: 220 }}>
                  <Select
                    value={localDoorModel?.label || ""}
                    onChange={(e) => {
                      const sw = currentModels.find(s => s.label === e.target.value)
                      setLocalDoorModel(sw || null)
                    }}
                    displayEmpty
                    sx={{
                      fontSize: "0.75rem",
                      borderRadius: 1.5,
                      height: 30,
                      fontWeight: 600,
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e50019" },
                    }}
                    renderValue={(selected) => {
                      if (!selected) return <em style={{ color: "#94a3b8" }}>{t("Μοντελο...")}</em>
                      const sw = currentModels.find(s => s.label === selected)
                      return (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {sw?.swatchUrl && (
                            <Box
                              component="img"
                              src={sw.swatchUrl}
                              sx={{ width: 16, height: 16, borderRadius: "3px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                              onError={(e) => { e.target.style.display = "none" }}
                            />
                          )}
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>{selected}</Typography>
                        </Stack>
                      )
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.78rem" }}>
                      <em>{t("Μοντελο...")}</em>
                    </MenuItem>
                    {currentModels.map((sw, idx) => (
                      <MenuItem key={idx} value={sw.label} sx={{ fontSize: "0.78rem" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 20, height: 20,
                              borderRadius: 0.5,
                              border: "1px solid #e2e8f0",
                              overflow: "hidden",
                              flexShrink: 0,
                              backgroundImage: sw.swatchUrl ? `url(${sw.swatchUrl})` : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              bgcolor: "#f1f5f9",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.78rem" }}>{sw.label}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {/* Color dropdown */}
            {localModel && currentColors.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0" }} />
                <FormControl size="small" sx={{ minWidth: 160, maxWidth: 220 }}>
                  <Select
                    value={localSwatch?.label || ""}
                    onChange={(e) => {
                      const sw = currentColors.find(s => s.label === e.target.value)
                      setLocalSwatch(sw || null)
                    }}
                    displayEmpty
                    sx={{
                      fontSize: "0.75rem",
                      borderRadius: 1.5,
                      height: 30,
                      fontWeight: 600,
                      bgcolor: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e50019" },
                    }}
                    renderValue={(selected) => {
                      if (!selected) return <em style={{ color: "#94a3b8" }}>{t("Χρωμα...")}</em>
                      const sw = currentColors.find(s => s.label === selected)
                      return (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {sw?.swatchUrl && (
                            <Box
                              component="img"
                              src={sw.swatchUrl}
                              sx={{ width: 16, height: 16, borderRadius: "3px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                              onError={(e) => { e.target.style.display = "none" }}
                            />
                          )}
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>{selected}</Typography>
                        </Stack>
                      )
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.78rem" }}>
                      <em>{t("Χρωμα...")}</em>
                    </MenuItem>
                    {currentColors.map((sw, idx) => (
                      <MenuItem key={idx} value={sw.label} sx={{ fontSize: "0.78rem" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 20, height: 20,
                              borderRadius: 0.5,
                              border: "1px solid #e2e8f0",
                              overflow: "hidden",
                              flexShrink: 0,
                              backgroundImage: sw.swatchUrl ? `url(${sw.swatchUrl})` : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              bgcolor: sw.hexColor || "#e2e8f0",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.78rem" }}>{sw.label}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {/* Custom RAL code — reference field, available on any product group.
                +€180 flat per unique code across the whole order (deduped). */}
            {ralEligible && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0" }} />
                <Tooltip title={t("Custom RAL: +€180 flat ανά μοναδικό κωδικό σε όλη την παραγγελία")} arrow placement="top">
                  <TextField
                    size="small"
                    value={localRalCode}
                    onChange={(e) => setLocalRalCode(e.target.value)}
                    placeholder={t("RAL...")}
                    sx={{
                      width: 110,
                      "& .MuiOutlinedInput-root": {
                        height: 30,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        bgcolor: "#fff",
                      },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#fbbf24" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d97706" },
                    }}
                    inputProps={{ maxLength: 24, style: { padding: "4px 8px" } }}
                  />
                </Tooltip>
              </>
            )}
          </Stack>
        )}
      </Box>

      <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden", flexDirection: "column" }}>
        {/* ── Product Grid ── */}
        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <ProductGrid
            selectedPosition={isCabinet ? position : null}
            priceGroup={localModel}
            swatch1={localSwatch}
            swatch2={null}
            doorModel={localDoorModel}
            customRalCode={localRalCode}
            onAddProduct={onAddProduct}
            kasoma={isCabinet ? kasoma : ""}
            pomolo={isCabinet ? effectivePomolo : ""}
            productMode={mode}
            surcharges={surcharges}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default AddBoxDialog
